import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Badge
} from '@mui/material'
import {
  Menu as MenuIcon,
  AccountCircle,
  ExitToApp,
  Settings,
  School,
  Notifications as NotificationsIcon
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import axios from 'axios'

const Header = ({ onSidebarToggle, sidebarOpen }) => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)
  const [notificationsCount, setNotificationsCount] = useState(0)

  // Cargar notificaciones no leídas
  useEffect(() => {
    const loadNotificationsCount = async () => {
      if (!user || (user.rol !== 'administrador' && user.rol !== 'editor')) {
        return
      }

      try {
        const token = localStorage.getItem('sgc_token')
        const response = await axios.get('/api/notificaciones/stats', {
          headers: { Authorization: `Bearer ${token}` }
        })
        setNotificationsCount(response.data.no_leidas)
      } catch (error) {
        console.error('Error al cargar notificaciones:', error)
      }
    }

    loadNotificationsCount()

    // Actualizar cada 60 segundos
    const interval = setInterval(loadNotificationsCount, 60000)
    return () => clearInterval(interval)
  }, [user])

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    handleProfileMenuClose()
    await logout()
  }

  const handleProfile = () => {
    handleProfileMenuClose()
    navigate('/perfil')
  }

  const handleSettings = () => {
    handleProfileMenuClose()
    navigate('/configuracion/sistema')
  }

  const handleNotifications = () => {
    navigate('/notificaciones')
  }

  const getRoleBadgeColor = (rol) => {
    switch (rol) {
      case 'administrador':
        return 'error'
      case 'rector':
        return 'secondary'
      case 'editor':
        return 'warning'
      case 'consultor':
        return 'info'
      default:
        return 'default'
    }
  }

  const getRoleLabel = (rol) => {
    switch (rol) {
      case 'administrador':
        return 'Admin'
      case 'rector':
        return 'Rector'
      case 'editor':
        return 'Editor'
      case 'consultor':
        return 'Consultor'
      default:
        return rol
    }
  }

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: 'primary.main'
      }}
    >
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="toggle sidebar"
          onClick={onSidebarToggle}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        <School sx={{ mr: 1 }} />
        
        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          SGC - ENSAS
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
            <Chip
              label={getRoleLabel(user?.rol)}
              color={getRoleBadgeColor(user?.rol)}
              size="small"
              variant="outlined"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
            />

            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {user?.nombre}
            </Typography>
          </Box>

          {/* Botón de notificaciones (solo para editores y administradores) */}
          {(user?.rol === 'administrador' || user?.rol === 'editor') && (
            <IconButton
              size="large"
              aria-label="notificaciones"
              onClick={handleNotifications}
              color="inherit"
            >
              <Badge badgeContent={notificationsCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}

          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="profile-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'secondary.main',
                fontSize: '0.875rem'
              }}
            >
              {user?.nombre?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
        </Box>

        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          onClick={handleProfileMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 200,
              '& .MuiAvatar-root': {
                width: 24,
                height: 24,
                ml: -0.5,
                mr: 1,
              }
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {user?.nombre}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
            <Chip
              label={getRoleLabel(user?.rol)}
              color={getRoleBadgeColor(user?.rol)}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Box>
          
          <Divider />
          
          <MenuItem onClick={handleProfile}>
            <ListItemIcon>
              <AccountCircle fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mi Perfil</ListItemText>
          </MenuItem>

          {(user?.rol === 'administrador' || user?.rol === 'editor') && (
            <MenuItem onClick={() => { handleProfileMenuClose(); handleNotifications(); }}>
              <ListItemIcon>
                <Badge badgeContent={notificationsCount} color="error">
                  <NotificationsIcon fontSize="small" />
                </Badge>
              </ListItemIcon>
              <ListItemText>Notificaciones</ListItemText>
            </MenuItem>
          )}

          {user?.rol === 'administrador' && (
            <MenuItem onClick={handleSettings}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              <ListItemText>Configuración</ListItemText>
            </MenuItem>
          )}

          <Divider />
          
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <ExitToApp fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cerrar Sesión</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  )
}

export default Header